import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Users, Building2, DollarSign, BarChart3, Activity,
  Search, Filter, RefreshCw, MoreVertical, Ban, CheckCircle,
  Trash2, Eye, Bell, Flag, Settings, ChevronRight, X,
  AlertTriangle, Info, Star, Wrench, Mail, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  deleted: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const PLAN_COLORS = {
  starter: 'bg-slate-500/20 text-slate-400',
  growth: 'bg-blue-500/20 text-blue-400',
  agency: 'bg-purple-500/20 text-purple-400',
};

const ANNOUNCEMENT_ICONS = {
  info: Info,
  warning: AlertTriangle,
  feature: Star,
  maintenance: Wrench,
};

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '', message: '', type: 'info', send_email: false
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Try to fetch admin stats - will fail if not admin
      try {
        const [statsRes, revenueRes, usersRes, flagsRes, announcementsRes, activityRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/stats`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/revenue`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/users?limit=50`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/feature-flags`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/announcements`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/activity-log?limit=20`, { headers }),
        ]);

        setStats(statsRes.data);
        setRevenue(revenueRes.data);
        setUsers(usersRes.data);
        setFeatureFlags(flagsRes.data);
        setAnnouncements(announcementsRes.data);
        setActivityLog(activityRes.data);
        setIsAdmin(true);
      } catch (error) {
        if (error.response?.status === 403) {
          setIsAdmin(false);
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSuspendUser = async (userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/suspend`,
        { reason: 'Suspended by admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User suspended');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User activated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate user');
    }
  };

  const handleToggleFeatureFlag = async (flagId, enabled) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${BACKEND_URL}/api/admin/feature-flags/${flagId}`,
        { enabled: !enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Feature ${enabled ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update feature flag');
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/admin/announcements`,
        announcementForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Announcement created');
      setShowAnnouncementModal(false);
      setAnnouncementForm({ title: '', message: '', type: 'info', send_email: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${BACKEND_URL}/api/admin/announcements/${announcementId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Announcement deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 flex items-center justify-center">
        <Card className="bg-[#052e22]/50 border-emerald-800/30 max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-emerald-300/70">
              You don't have permission to access the admin panel.
              Contact a platform administrator if you need access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Shield className="w-8 h-8 text-red-400" />
            Admin Panel
          </h1>
          <p className="text-emerald-300/70">Platform administration and management</p>
        </div>
        <Button
          onClick={() => setShowAnnouncementModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Bell className="w-4 h-4 mr-2" />
          New Announcement
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'overview', icon: BarChart3, label: 'Overview' },
          { id: 'users', icon: Users, label: 'Users' },
          { id: 'features', icon: Flag, label: 'Features' },
          { id: 'announcements', icon: Bell, label: 'Announcements' },
          { id: 'activity', icon: Activity, label: 'Activity' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-emerald-600 text-white'
                : 'bg-[#052e22]/50 text-emerald-300/70 hover:bg-emerald-900/30'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-[#052e22]/50 border-emerald-800/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Users className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats?.total_users || 0}</p>
                      <p className="text-xs text-emerald-300/70">Total Users</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#052e22]/50 border-emerald-800/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats?.active_users || 0}</p>
                      <p className="text-xs text-emerald-300/70">Active (30d)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#052e22]/50 border-emerald-800/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <Building2 className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats?.total_workspaces || 0}</p>
                      <p className="text-xs text-emerald-300/70">Workspaces</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#052e22]/50 border-emerald-800/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <DollarSign className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{formatCurrency(revenue?.total_revenue)}</p>
                      <p className="text-xs text-emerald-300/70">Total Revenue</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Revenue & Usage */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-[#052e22]/50 border-emerald-800/30">
                <CardHeader className="border-b border-emerald-800/30">
                  <CardTitle className="text-lg font-semibold text-white">Revenue Overview</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-300/70">Monthly Revenue</span>
                    <span className="text-white font-bold">{formatCurrency(revenue?.monthly_revenue)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-300/70">Active Subscriptions</span>
                    <span className="text-white font-bold">{revenue?.active_subscriptions || 0}</span>
                  </div>
                  <div className="border-t border-emerald-800/30 pt-4">
                    <p className="text-sm text-emerald-300/70 mb-2">Revenue by Plan</p>
                    {revenue?.revenue_by_plan && Object.entries(revenue.revenue_by_plan).map(([plan, amount]) => (
                      <div key={plan} className="flex justify-between items-center py-1">
                        <Badge className={PLAN_COLORS[plan] || PLAN_COLORS.starter}>{plan}</Badge>
                        <span className="text-white">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#052e22]/50 border-emerald-800/30">
                <CardHeader className="border-b border-emerald-800/30">
                  <CardTitle className="text-lg font-semibold text-white">Platform Usage</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-300/70">Total Leads</span>
                    <span className="text-white font-bold">{stats?.total_leads || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-300/70">Total Funnels</span>
                    <span className="text-white font-bold">{stats?.total_funnels || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-300/70">AI Generations</span>
                    <span className="text-white font-bold">{stats?.total_ai_generations || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-300/70">Emails Sent</span>
                    <span className="text-white font-bold">{stats?.total_emails_sent || 0}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div
            key="users"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-white">All Users</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-[#0a3d2e]/50 border border-emerald-800/30 rounded-lg text-white placeholder-emerald-500/50 text-sm focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-emerald-800/30">
                        <th className="text-left p-4 text-emerald-300/70 font-medium">User</th>
                        <th className="text-left p-4 text-emerald-300/70 font-medium">Plan</th>
                        <th className="text-left p-4 text-emerald-300/70 font-medium">Status</th>
                        <th className="text-left p-4 text-emerald-300/70 font-medium">Leads</th>
                        <th className="text-left p-4 text-emerald-300/70 font-medium">Joined</th>
                        <th className="text-right p-4 text-emerald-300/70 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="border-b border-emerald-800/30 hover:bg-emerald-900/20">
                          <td className="p-4">
                            <div>
                              <p className="text-white font-medium">{user.name}</p>
                              <p className="text-sm text-emerald-300/70">{user.email}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge className={PLAN_COLORS[user.plan] || PLAN_COLORS.starter}>
                              {user.plan}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Badge className={STATUS_COLORS[user.status] || STATUS_COLORS.active}>
                              {user.status}
                            </Badge>
                          </td>
                          <td className="p-4 text-white">{user.total_leads}</td>
                          <td className="p-4 text-emerald-300/70 text-sm">{formatDate(user.created_at)}</td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {user.status === 'active' ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSuspendUser(user.id)}
                                  className="text-red-400 hover:bg-red-900/30"
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleActivateUser(user.id)}
                                  className="text-green-400 hover:bg-green-900/30"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Features Tab */}
        {activeTab === 'features' && (
          <motion.div
            key="features"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30">
                <CardTitle className="text-lg font-semibold text-white">Feature Flags</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-emerald-800/30">
                  {featureFlags.map((flag) => (
                    <div
                      key={flag.id}
                      className="flex items-center justify-between p-4 hover:bg-emerald-900/20"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${flag.enabled ? 'bg-green-500/20' : 'bg-slate-500/20'}`}>
                          <Flag className={`w-5 h-5 ${flag.enabled ? 'text-green-400' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{flag.name}</p>
                          <p className="text-sm text-emerald-300/70">{flag.description}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleFeatureFlag(flag.id, flag.enabled)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          flag.enabled ? 'bg-green-500' : 'bg-slate-600'
                        }`}
                      >
                        <span
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            flag.enabled ? 'translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <motion.div
            key="announcements"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30">
                <CardTitle className="text-lg font-semibold text-white">Announcements</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {announcements.length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="w-12 h-12 text-emerald-800 mx-auto mb-3" />
                    <p className="text-emerald-300/50">No announcements yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-emerald-800/30">
                    {announcements.map((ann) => {
                      const Icon = ANNOUNCEMENT_ICONS[ann.type] || Info;
                      return (
                        <div
                          key={ann.id}
                          className="flex items-start gap-4 p-4 hover:bg-emerald-900/20"
                        >
                          <div className={`p-2 rounded-lg ${
                            ann.type === 'warning' ? 'bg-amber-500/20' :
                            ann.type === 'feature' ? 'bg-purple-500/20' :
                            ann.type === 'maintenance' ? 'bg-red-500/20' : 'bg-blue-500/20'
                          }`}>
                            <Icon className={`w-5 h-5 ${
                              ann.type === 'warning' ? 'text-amber-400' :
                              ann.type === 'feature' ? 'text-purple-400' :
                              ann.type === 'maintenance' ? 'text-red-400' : 'text-blue-400'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <p className="text-white font-medium">{ann.title}</p>
                            <p className="text-sm text-emerald-300/70 mt-1">{ann.message}</p>
                            <p className="text-xs text-emerald-300/50 mt-2">
                              {formatDate(ann.created_at)} by {ann.created_by}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAnnouncement(ann.id)}
                            className="text-red-400 hover:bg-red-900/30"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <motion.div
            key="activity"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30">
                <CardTitle className="text-lg font-semibold text-white">Admin Activity Log</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {activityLog.length === 0 ? (
                  <div className="text-center py-12">
                    <Activity className="w-12 h-12 text-emerald-800 mx-auto mb-3" />
                    <p className="text-emerald-300/50">No admin actions yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-emerald-800/30">
                    {activityLog.map((action) => (
                      <div
                        key={action.id}
                        className="flex items-start gap-4 p-4 hover:bg-emerald-900/20"
                      >
                        <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                          <Shield className="w-4 h-4 text-red-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white">
                            <span className="font-medium">{action.admin_email}</span>
                            {' '}{action.action.replace(/_/g, ' ')}{' '}
                            <span className="text-emerald-400">{action.target_type}</span>
                          </p>
                          <p className="text-xs text-emerald-300/50 mt-1">
                            {formatDate(action.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Announcement Modal */}
      <AnimatePresence>
        {showAnnouncementModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAnnouncementModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a3d2e] border border-emerald-800/30 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">New Announcement</h2>
                <button
                  onClick={() => setShowAnnouncementModal(false)}
                  className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-emerald-400" />
                </button>
              </div>
              <form onSubmit={handleCreateAnnouncement} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">Title *</label>
                  <input
                    type="text"
                    required
                    value={announcementForm.title}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                    placeholder="Announcement title"
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">Message *</label>
                  <textarea
                    required
                    value={announcementForm.message}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                    placeholder="Announcement message..."
                    rows={4}
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">Type</label>
                  <select
                    value={announcementForm.type}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, type: e.target.value })}
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  >
                    <option value="info">Info</option>
                    <option value="feature">New Feature</option>
                    <option value="warning">Warning</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-emerald-300">
                  <input
                    type="checkbox"
                    checked={announcementForm.send_email}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, send_email: e.target.checked })}
                    className="w-4 h-4 rounded border-emerald-500 bg-transparent"
                  />
                  Send email notification to all users
                </label>
                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Create Announcement
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;
